import React from 'react'
import {
  TextCell,
  InfoPillCell,
  DateCell,
  CopyableTextCell,
  COLUMN_SORT_SETTING,
  COLUMN_TYPES,
  SORT_DIRECTION,
} from '@adc/datatable'
import { statusColorMapper } from '../../components/invitation/utils'
import DrawerCell from './drawerCell'
import RadioCell from './radioCell'

const currentStandardColProps = {
  isResizable: true,
  isReorderable: true,
  isVisible: true,
  searchable: false,
  align: 'left',
  flexGrow: 0,
  sort: COLUMN_SORT_SETTING.NOT_SORTED,
  width: 150,
}

/**
 * Columns used for CSV export. Flat text-only columns without interactive cells.
 * NOTE: getCSVExportData reads row[column.field] directly — it does NOT use accessor.
 * Data must be pre-processed before passing to getCSVExportData so that row fields
 * match these column.field values.
 */
export const columnsForDownload = [
  {
    id: 'party_id',
    title: 'Party ID',
    field: 'party_id',
    colProps: { isVisible: true },
  },
  {
    id: 'first_name',
    title: 'First Name',
    field: 'first_name',
    colProps: { isVisible: true },
  },
  {
    id: 'last_name',
    title: 'Last Name',
    field: 'last_name',
    colProps: { isVisible: true },
  },
  {
    id: 'email',
    title: 'Email',
    field: 'email',
    colProps: { isVisible: true },
  },
  {
    id: 'status',
    title: 'Status',
    field: 'status',
    colProps: { isVisible: true },
  },
  {
    id: 'groups',
    title: 'Groups',
    field: 'groups',
    colProps: { isVisible: true },
  },
  {
    id: 'external_orgs',
    title: 'External Orgs',
    field: 'external_orgs',
    colProps: { isVisible: true },
  },
  {
    id: 'created_date',
    title: 'Created Date',
    field: 'created_date',
    colProps: { isVisible: true },
  },
  {
    id: 'updated_date',
    title: 'Updated Date',
    field: 'updated_date',
    colProps: { isVisible: true },
  },
]

/**
 * Transforms raw OrganizationMember rows into flat objects matching columnsForDownload fields.
 */
export const transformRowsForDownload = rows =>
  rows.map(row => ({
    ...row,
    status: row.is_active ? 'Active' : 'Inactive',
    groups: Array.isArray(row.groups)
      ? row.groups.join(', ')
      : row.groups || '',
    external_orgs: (() => {
      const orgs = row.external_organization_accesses
      if (!orgs || !Array.isArray(orgs) || orgs.length === 0) return ''
      return orgs
        .map(org =>
          typeof org === 'object' && org.name ? org.name : `Org ${org}`
        )
        .join(', ')
    })(),
  }))

export const columnsForDownloadWithOrgId = [
  columnsForDownload[0], // party_id
  {
    id: 'organization_name',
    title: 'Organization Name',
    field: 'organization_name',
    accessor: row => row._organization_name || '',
    colProps: { isVisible: true },
  },
  {
    id: 'organization_id',
    title: 'Organization ID',
    field: 'organization_id',
    accessor: row => row.organization_id || '',
    colProps: { isVisible: true },
  },
  ...columnsForDownload.slice(1),
]
export const generateColumns = ({
  sortingFilter,
  actionHandlers = {},
  includeOrganizationId = false,
  organizationNameMap = null,
  showRadioSelect = true,
}) => {
  const cols = [
    ...(showRadioSelect
      ? [
          {
            id: 'radio_select',
            field: 'radio_select',
            title: '',
            accessor: row => row,
            Cell: props => (
              <RadioCell
                {...props}
                selectedMemberId={actionHandlers?.selectedMemberId}
                onSelectMember={actionHandlers?.onSelectMember}
              />
            ),
            colProps: {
              width: 40,
              fixed: true,
              isResizable: false,
              isReorderable: false,
              isVisible: true,
            },
          },
        ]
      : []),
    ...(includeOrganizationId
      ? [
          {
            id: 'organization_name',
            title: 'Organization Name',
            Cell: props => <TextCell {...props} />,
            apiRef: 'organization_name',
            accessor: row => {
              if (!organizationNameMap || !row.organization_id) return ''
              return organizationNameMap.get(String(row.organization_id)) || ''
            },
            colProps: {
              ...currentStandardColProps,
              width: 220,
              searchable: false,
              sort: COLUMN_SORT_SETTING.NOT_SORTABLE,
            },
          },
          {
            id: 'organization_id',
            title: 'Organization ID',
            Cell: props => <CopyableTextCell {...props} />,
            apiRef: 'organization_id',
            sortField: 'organization_id',
            accessor: row => row.organization_id || '',
            colProps: {
              ...currentStandardColProps,
              width: 200,
              isVisible: false,
              sort: COLUMN_SORT_SETTING.NOT_SORTED,
            },
          },
        ]
      : []),
    {
      id: 'party_id',
      title: 'Party Id',
      Cell: props => <CopyableTextCell {...props} />,
      apiRef: 'party_id',
      sortField: 'party_id',
      accessor: row => row.party_id,
      colProps: {
        ...currentStandardColProps,
        width: 250,
        sort: COLUMN_SORT_SETTING.NOT_SORTED,
      },
    },
    {
      id: 'first_name',
      title: 'First Name',
      Cell: props => <CopyableTextCell {...props} />,
      apiRef: 'first_name',
      sortField: 'first_name.keyword',
      accessor: row => row.first_name,
      colProps: {
        ...currentStandardColProps,
        width: 250,
        searchable: true,
        sort: COLUMN_SORT_SETTING.NOT_SORTED,
      },
    },
    {
      id: 'last_name',
      title: 'Last Name',
      Cell: props => <CopyableTextCell {...props} />,
      apiRef: 'last_name',
      sortField: 'last_name.keyword',
      accessor: row => row.last_name,
      colProps: {
        ...currentStandardColProps,
        width: 250,
        searchable: true,
        sort: COLUMN_SORT_SETTING.NOT_SORTED,
      },
    },
    {
      id: 'email',
      title: 'Email',
      Cell: props => <CopyableTextCell {...props} />,
      apiRef: 'email',
      sortField: 'email.keyword',
      accessor: row => row.email,
      colProps: {
        ...currentStandardColProps,
        width: 250,
        searchable: true,
        sort: COLUMN_SORT_SETTING.NOT_SORTED,
      },
    },
    {
      id: 'status',
      title: 'Status',
      Cell: props => <TextCell {...props} />,
      apiRef: 'status',
      sortField: 'status.keyword',
      accessor: row => (row.is_active ? 'Active' : 'Inactive'),
      colProps: {
        ...currentStandardColProps,
        width: 250,
        searchable: true,
        sort: COLUMN_SORT_SETTING.NOT_SORTED,
      },
    },
    {
      id: 'groups',
      title: 'Groups',
      Cell: props => {
        // eslint-disable-next-line react/prop-types
        const { value } = props
        if (!value) {
          return <span />
        }
        return (
          <span title={value}>
            <InfoPillCell {...props} colorMapper={statusColorMapper} />
          </span>
        )
      },
      apiRef: 'groups',
      sortField: 'groups.keyword',
      accessor: row =>
        Array.isArray(row.groups) ? row.groups.join(', ') : row.groups || '',
      colProps: {
        ...currentStandardColProps,
        width: 250,
        searchable: true,
        sort: COLUMN_SORT_SETTING.NOT_SORTED,
      },
    },
    {
      id: 'external_orgs',
      title: 'External Orgs',
      Cell: props => {
        // eslint-disable-next-line react/prop-types
        const { value } = props
        if (!value) {
          return <span />
        }
        return (
          <span title={value}>
            <InfoPillCell {...props} />
          </span>
        )
      },
      apiRef: 'external_organization_accesses',
      accessor: row => {
        const orgs = row.external_organization_accesses
        if (!orgs || !Array.isArray(orgs) || orgs.length === 0) return ''
        return orgs
          .map(org =>
            typeof org === 'object' && org.name ? org.name : `Org ${org}`
          )
          .join(', ')
      },
      colProps: {
        ...currentStandardColProps,
        width: 200,
        searchable: false,
        sort: COLUMN_SORT_SETTING.NOT_SORTABLE,
      },
    },
    {
      id: 'created_date',
      title: 'Created Date',
      Cell: props => <DateCell {...props} format='MMM DD, YYYY' />,
      apiRef: 'created_date',
      accessor: row => row.created_date,
      colProps: {
        ...currentStandardColProps,
        sort: COLUMN_SORT_SETTING.SORTED,
      },
    },
    {
      id: 'updated_date',
      title: 'Updated Date',
      Cell: props => <DateCell {...props} format='MMM DD, YYYY' />,
      apiRef: 'updated_date',
      accessor: row => row.updated_date,
      colProps: currentStandardColProps,
    },
    {
      id: '__actions__',
      title: 'Actions',
      type: COLUMN_TYPES.ACTION,
      accessor: row => row,
      Cell: props => (
        <DrawerCell onClick={actionHandlers?.onViewClick} {...props} />
      ),
      colProps: {
        ...currentStandardColProps,
        width: 70,
        fixedRight: true,
        align: 'center',
        isResizable: false,
        isReorderable: false,
        isVisible: false,
      },
    },
  ].map(column => {
    if (sortingFilter.columnKey === column.id) {
      column.colProps.sort = COLUMN_SORT_SETTING.SORTED
      column.colProps.sortAscending =
        sortingFilter.direction === SORT_DIRECTION.ASC
      column.colProps.initialSortDirection = sortingFilter.direction
    }
    column.field = column.field || column.id // TODO: reconcile these - datatable uses both - needs to be consolidated
    column.sortField = column.sortField || column.apiRef // assume field if sortField is not provided
    return column
  })

  return cols
}
